import React, { useState, useRef, useEffect } from 'react';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

// Mock function to simulate image upload to server
const uploadImageToServer = async (file: File): Promise<string> => {
  return new Promise((resolve) => {
    // In a real app, this would be an API call to upload the image
    // For now, we'll create a data URL to simulate the upload
    const reader = new FileReader();
    reader.onload = () => {
      // Simulate network delay
      setTimeout(() => {
        resolve(reader.result as string);
      }, 500);
    };
    reader.readAsDataURL(file);
  });
};

const SimpleRichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text here...',
  disabled = false,
  className = '',
  error = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!value);
  const [isUploading, setIsUploading] = useState(false);
  const [isFormatting, setIsFormatting] = useState(true);
  
  // Track resize state
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
  
  // Initialize editor with content when value changes externally
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
      setIsEmpty(!value || value === '' || value === '<br>' || value === '<div><br></div>');
    }
  }, [value]);
  
  // Handle input changes
  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      setIsEmpty(!content || content === '<br>' || content === '<div><br></div>');
      onChange(content);
    }
  };
  
  // Handle paste to strip formatting
  const handlePaste = (e: React.ClipboardEvent) => {
    if (disabled) return;
    
    // Prevent the default paste behavior
    e.preventDefault();
    
    // Get text from clipboard
    const text = e.clipboardData.getData('text/plain');
    
    // Insert text at cursor position
    document.execCommand('insertText', false, text);
  };
  
  // Format commands
  const formatText = (command: string, value?: string) => {
    if (disabled) return;
    document.execCommand(command, false, value);
    
    // Get the updated HTML content and pass it to onChange
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    
    // Refocus the editor after command execution
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };
  
  // Handle image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !e.target.files || e.target.files.length === 0) return;
    
    try {
      setIsUploading(true);
      const file = e.target.files[0];
      
      // Upload image to server (mock function)
      const imageUrl = await uploadImageToServer(file);
      
      // Make sure the editor has focus before inserting the image
      if (editorRef.current) {
        // Focus the editor
        editorRef.current.focus();
        
        // If there's no selection or cursor position, move to the end
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount || !editorRef.current.contains(selection.anchorNode)) {
          // Create a range at the end of the editor content
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false); // collapse to end
          
          // Apply this range as the current selection
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
        
        // Insert a resizable image
        const imgHtml = `<img src="${imageUrl}" class="resizable" style="width: auto; max-width: 100%;" />`;
        document.execCommand('insertHTML', false, imgHtml);
        
        // Get the updated HTML content and pass it to onChange
        setTimeout(() => {
          onChange(editorRef.current!.innerHTML);
          setIsEmpty(false);
          
          // Make the newly inserted image resizable
          setupResizableImages();
        }, 100);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Handle video embedding
  const handleVideoEmbed = () => {
    if (disabled) return;
    
    const videoUrl = prompt('Enter video URL (YouTube, Vimeo, etc.):');
    if (!videoUrl) return;
    
    let embedCode = '';
    
    // Handle YouTube URLs
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      let videoId = '';
      
      if (videoUrl.includes('youtube.com/watch?v=')) {
        videoId = videoUrl.split('v=')[1];
        const ampersandPosition = videoId.indexOf('&');
        if (ampersandPosition !== -1) {
          videoId = videoId.substring(0, ampersandPosition);
        }
      } else if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1];
      }
      
      if (videoId) {
        embedCode = `<div class="video-embed resizable" style="width: 560px; height: 315px; position: relative;">
          <iframe width="100%" height="100%" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen style="position: absolute; top: 0; left: 0;"></iframe>
          <div class="resize-handle"></div>
        </div>`;
      }
    }
    // Handle Vimeo URLs
    else if (videoUrl.includes('vimeo.com')) {
      const vimeoId = videoUrl.split('vimeo.com/')[1];
      if (vimeoId) {
        embedCode = `<div class="video-embed resizable" style="width: 560px; height: 315px; position: relative;">
          <iframe width="100%" height="100%" src="https://player.vimeo.com/video/${vimeoId}" frameborder="0" allowfullscreen style="position: absolute; top: 0; left: 0;"></iframe>
          <div class="resize-handle"></div>
        </div>`;
      }
    }
    // For other URLs, just create a direct link
    else {
      embedCode = `<a href="${videoUrl}" target="_blank" rel="noopener noreferrer">${videoUrl}</a>`;
    }
    
    if (embedCode) {
      // Make sure the editor has focus before inserting the video
      if (editorRef.current) {
        // Focus the editor
        editorRef.current.focus();
        
        // If there's no selection or cursor position, move to the end
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount || !editorRef.current.contains(selection.anchorNode)) {
          // Create a range at the end of the editor content
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false); // collapse to end
          
          // Apply this range as the current selection
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
        
        document.execCommand('insertHTML', false, embedCode);
        
        // Get the updated HTML content and pass it to onChange
        setTimeout(() => {
          onChange(editorRef.current!.innerHTML);
          setIsEmpty(false);
          
          // Make the newly inserted video resizable
          setupResizableVideos();
        }, 100);
      }
    }
  };
  
  // Setup resizable images
  const setupResizableImages = () => {
    if (!editorRef.current) return;
    
    const images = editorRef.current.querySelectorAll('img.resizable');
    
    images.forEach((img) => {
      if (!(img as HTMLElement).dataset.resizeInitialized) {
        // Mark as initialized
        (img as HTMLElement).dataset.resizeInitialized = 'true';
        
        // Add click handler to select the image
        img.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Deselect any previously selected element
          if (selectedElement && selectedElement !== img) {
            selectedElement.classList.remove('selected');
            const oldHandle = selectedElement.querySelector('.resize-handle');
            if (oldHandle) oldHandle.remove();
          }
          
          // Select this image
          setSelectedElement(img as HTMLElement);
          img.classList.add('selected');
          
          // Add resize handle if it doesn't exist
          if (!img.nextElementSibling?.classList?.contains('resize-handle')) {
            const handle = document.createElement('div');
            handle.className = 'resize-handle';
            handle.style.position = 'absolute';
            handle.style.right = '-8px';
            handle.style.bottom = '-8px';
            handle.style.width = '16px';
            handle.style.height = '16px';
            handle.style.background = '#2563eb';
            handle.style.cursor = 'nwse-resize';
            handle.style.borderRadius = '3px';
            handle.style.zIndex = '999';
            
            // Wrap the image in a relative div if not already
            if (img.parentElement?.style.position !== 'relative') {
              const wrapper = document.createElement('div');
              wrapper.style.position = 'relative';
              wrapper.style.display = 'inline-block';
              img.parentNode?.insertBefore(wrapper, img);
              wrapper.appendChild(img);
              wrapper.appendChild(handle);
            } else {
              img.parentElement?.appendChild(handle);
            }
            
            // Add resize functionality
            handle.addEventListener('mousedown', initResize);
          }
        });
      }
    });
  };
  
  // Setup resizable videos
  const setupResizableVideos = () => {
    if (!editorRef.current) return;
    
    const videos = editorRef.current.querySelectorAll('.video-embed.resizable');
    
    videos.forEach((video) => {
      if (!(video as HTMLElement).dataset.resizeInitialized) {
        // Mark as initialized
        (video as HTMLElement).dataset.resizeInitialized = 'true';
        
        // Add click handler to select the video
        video.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Deselect any previously selected element
          if (selectedElement && selectedElement !== video) {
            selectedElement.classList.remove('selected');
          }
          
          // Select this video
          setSelectedElement(video as HTMLElement);
          video.classList.add('selected');
          
          // Make sure it has a resize handle
          if (!video.querySelector('.resize-handle')) {
            const handle = document.createElement('div');
            handle.className = 'resize-handle';
            video.appendChild(handle);
            
            // Add resize functionality
            handle.addEventListener('mousedown', initResize);
          }
        });
      }
    });
  };
  
  // Initialize resize operation
  const initResize = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!selectedElement) return;
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = selectedElement.offsetWidth;
    const startHeight = selectedElement.offsetHeight;
    const aspectRatio = startWidth / startHeight;
    
    // Add resize event listeners
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);
    
    // Resize function
    function resize(e: MouseEvent) {
      if (!selectedElement) return;
      
      const width = startWidth + (e.clientX - startX);
      
      // For images, maintain aspect ratio
      if (selectedElement.tagName === 'IMG') {
        const height = width / aspectRatio;
        selectedElement.style.width = `${width}px`;
        selectedElement.style.height = `${height}px`;
      } 
      // For videos, maintain 16:9 aspect ratio
      else if (selectedElement.classList.contains('video-embed')) {
        const height = width * 0.5625; // 16:9 aspect ratio
        selectedElement.style.width = `${width}px`;
        selectedElement.style.height = `${height}px`;
      }
    }
    
    // Stop resize function
    function stopResize() {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
      
      // Update content
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
    }
  };
  
  // Handle clicks outside of media elements to deselect them
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectedElement && 
          e.target instanceof Node && 
          !selectedElement.contains(e.target) && 
          (!selectedElement.parentElement || !selectedElement.parentElement.contains(e.target))) {
        selectedElement.classList.remove('selected');
        setSelectedElement(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [selectedElement]);
  
  // Setup resizable media when editor content changes
  useEffect(() => {
    if (disabled || !editorRef.current) return;
    
    setupResizableImages();
    setupResizableVideos();
    
    // Set up a mutation observer to detect when new images or videos are added
    const observer = new MutationObserver(() => {
      setupResizableImages();
      setupResizableVideos();
    });
    
    // Start observing the editor for changes
    observer.observe(editorRef.current, { childList: true, subtree: true });
    
    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [disabled]);
  
  return (
    <div className="rich-text-editor">
      {isFormatting && (
      <div className="bg-gray-50 px-3 py-2 border border-gray-300 rounded-t-md flex flex-wrap gap-2">
        <button 
          type="button" 
          className={`px-2 py-1 text-sm ${disabled ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-100'} border border-gray-300 rounded`}
          onClick={() => formatText('bold')}
          disabled={disabled}
          title="Bold"
        >
          <b>B</b>
        </button>
        <button 
          type="button" 
          className={`px-2 py-1 text-sm ${disabled ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-100'} border border-gray-300 rounded`}
          onClick={() => formatText('italic')}
          disabled={disabled}
          title="Italic"
        >
          <i>I</i>
        </button>
        <button 
          type="button" 
          className={`px-2 py-1 text-sm ${disabled ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-100'} border border-gray-300 rounded`}
          onClick={() => formatText('underline')}
          disabled={disabled}
          title="Underline"
        >
          <u>U</u>
        </button>
        <button 
          type="button" 
          className={`px-2 py-1 text-sm ${disabled ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-100'} border border-gray-300 rounded`}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
          title="Upload Image"
        >
          <span className="text-xs">{isUploading ? 'Uploading...' : 'Upload IMG'}</span>
          <input 
            type="file" 
            ref={fileInputRef}
            className="hidden" 
            accept="image/*"
            onChange={handleImageUpload}
            disabled={disabled}
          />
        </button>
        <button 
          type="button" 
          className={`px-2 py-1 text-sm ${disabled ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-100'} border border-gray-300 rounded`}
          onClick={handleVideoEmbed}
          disabled={disabled}
          title="Embed Video"
        >
          <span className="text-xs">Video</span>
        </button>
        <button 
          type="button" 
          className={`px-2 py-1 text-sm ${disabled ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-100'} border border-gray-300 rounded`}
          onClick={() => formatText('insertUnorderedList')}
          disabled={disabled}
          title="Bullet List"
        >
          • List
        </button>
        <button 
          type="button" 
          className={`px-2 py-1 text-sm ${disabled ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-100'} border border-gray-300 rounded`}
          onClick={() => formatText('removeFormat')}
          disabled={disabled}
          title="Clear Formatting"
        >
          Clear
        </button>
      </div>
      )}
      
      <div className="relative">
        <div
          ref={editorRef}
          className={`${className} px-3 py-2 border ${isFormatting ? 'border-t-0' : ''} ${error ? 'border-red-500' : 'border-gray-300'} rounded-${isFormatting ? 'b' : ''}md min-h-[100px] focus:outline-none ${isFocused ? 'ring-2 ring-primary ring-opacity-50' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
          contentEditable={!disabled}
          dangerouslySetInnerHTML={{ __html: value || '' }}
          onInput={handleInput}
          onPaste={handlePaste}
          onFocus={() => { setIsFocused(true); setIsEmpty(false); }}
          onBlur={() => setIsFocused(false)}
          style={{ minHeight: '100px' }}
        />
        {isEmpty && !isFocused && (
          <div className="absolute top-0 left-0 p-3 text-gray-400 pointer-events-none">
            {placeholder}
          </div>
        )}
        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleRichTextEditor;
